// ===========================================
// Exportar calendario a formato iCal (.ics)
// RFC 5545 compliant
// ===========================================

import type { VencimientoConCliente } from '@/types/vencimientos'
import type { EventoCalendario } from '@/core/types/calendario'
import { parseFechaEvento } from '@/lib/fechaEvento'

/**
 * Escape special characters per RFC 5545
 */
const escapeIcal = (text: string): string =>
  text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')

/**
 * Format date to iCal VALUE=DATE (YYYYMMDD)
 */
const toIcalDate = (dateStr: string): string =>
  dateStr.replace(/-/g, '').substring(0, 8)

/**
 * Format datetime to iCal DATETIME (YYYYMMDDTHHMMSS)
 */
const toIcalDateTime = (dateStr: string): string => {
  // `parseFechaEvento`, no `new Date`: la hora se guardó sin zona y con `new Date` el .ics
  // saldría 3 horas antes de lo que el usuario cargó.
  const d = parseFechaEvento(dateStr)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
}

/**
 * Map vencimiento estado to iCal STATUS
 */
const vencimientoStatus = (estado: string | null): string => {
  switch (estado) {
    case 'presentado':
    case 'pagado':
    case 'plan_pago':
      return 'CONFIRMED'
    case 'vencido':
      return 'CANCELLED'
    default:
      return 'NEEDS-ACTION'
  }
}

/**
 * Map evento estado to iCal STATUS
 */
const eventoStatus = (estado: string): string => {
  switch (estado) {
    case 'confirmado':
    case 'completado':
      return 'CONFIRMED'
    case 'cancelado':
      return 'CANCELLED'
    default:
      return 'TENTATIVE'
  }
}

/**
 * Generate VEVENT block for a vencimiento
 */
const vencimientoToVevent = (v: VencimientoConCliente): string => {
  if (!v.fecha_vencimiento || !v.id) return ''

  const fecha = toIcalDate(v.fecha_vencimiento)
  // All-day event: DTEND is next day
  const parts = v.fecha_vencimiento.split('-')
  const nextDay = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]) + 1)
  const fechaFin = `${nextDay.getFullYear()}${String(nextDay.getMonth() + 1).padStart(2, '0')}${String(nextDay.getDate()).padStart(2, '0')}`

  const summary = escapeIcal(`${v.tipo_impuesto ?? 'Vencimiento'} - ${v.cliente_nombre ?? 'Sin cliente'}`)
  const desc = escapeIcal(
    [
      `Impuesto: ${v.tipo_impuesto ?? '-'}`,
      `Cliente: ${v.cliente_nombre ?? '-'}`,
      `Periodo: ${v.periodo ?? '-'}`,
      `Estado: ${v.estado ?? 'pendiente'}`,
      v.observaciones ? `Notas: ${v.observaciones}` : '',
    ]
      .filter(Boolean)
      .join('\n'),
  )

  const lines = [
    'BEGIN:VEVENT',
    `UID:vencimiento-${v.id}@brainhub`,
    `DTSTART;VALUE=DATE:${fecha}`,
    `DTEND;VALUE=DATE:${fechaFin}`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${desc}`,
    `STATUS:${vencimientoStatus(v.estado)}`,
    `CATEGORIES:Vencimiento Fiscal`,
    'END:VEVENT',
  ]
  return lines.join('\r\n')
}

/**
 * Generate VEVENT block for a calendar event
 */
const eventoToVevent = (e: EventoCalendario): string => {
  const lines: string[] = [
    'BEGIN:VEVENT',
    `UID:evento-${e.id}@brainhub`,
  ]

  if (e.todo_el_dia) {
    const fecha = toIcalDate(e.fecha_inicio)
    lines.push(`DTSTART;VALUE=DATE:${fecha}`)
    if (e.fecha_fin) {
      const parts = e.fecha_fin.split('T')[0].split('-')
      const nextDay = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]) + 1)
      const fechaFin = `${nextDay.getFullYear()}${String(nextDay.getMonth() + 1).padStart(2, '0')}${String(nextDay.getDate()).padStart(2, '0')}`
      lines.push(`DTEND;VALUE=DATE:${fechaFin}`)
    } else {
      const parts = e.fecha_inicio.split('T')[0].split('-')
      const nextDay = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]) + 1)
      const fechaFin = `${nextDay.getFullYear()}${String(nextDay.getMonth() + 1).padStart(2, '0')}${String(nextDay.getDate()).padStart(2, '0')}`
      lines.push(`DTEND;VALUE=DATE:${fechaFin}`)
    }
  } else {
    lines.push(`DTSTART:${toIcalDateTime(e.fecha_inicio)}`)
    if (e.fecha_fin) {
      lines.push(`DTEND:${toIcalDateTime(e.fecha_fin)}`)
    }
  }

  lines.push(`SUMMARY:${escapeIcal(e.titulo)}`)

  if (e.descripcion) {
    lines.push(`DESCRIPTION:${escapeIcal(e.descripcion)}`)
  }

  if (e.ubicacion) {
    lines.push(`LOCATION:${escapeIcal(e.ubicacion)}`)
  }

  lines.push(`STATUS:${eventoStatus(e.estado)}`)
  lines.push(`CATEGORIES:${e.tipo}`)
  lines.push('END:VEVENT')

  return lines.join('\r\n')
}

/**
 * Generate full .ics calendar content
 */
export const generarIcal = (
  vencimientos: VencimientoConCliente[],
  eventos: EventoCalendario[],
): string => {
  const header = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Menta//Calendario Fiscal//ES',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:Menta - Calendario Fiscal`,
    `X-WR-TIMEZONE:America/Argentina/Buenos_Aires`,
  ].join('\r\n')

  const vevents = [
    ...vencimientos.map(vencimientoToVevent).filter(Boolean),
    ...eventos.map(eventoToVevent),
  ].join('\r\n')

  return `${header}\r\n${vevents}\r\nEND:VCALENDAR\r\n`
}

/**
 * Download .ics file
 */
export const descargarIcal = (
  vencimientos: VencimientoConCliente[],
  eventos: EventoCalendario[],
  filename?: string,
): void => {
  const content = generarIcal(vencimientos, eventos)
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename ?? `menta-calendario-${new Date().toISOString().split('T')[0]}.ics`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
